/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as Msdyn365 from '@msdyn365-commerce/core';
import { Node } from '@msdyn365-commerce-modules/utilities';
import React from 'react';
import DobbiesModal from '../../../../modules/dobbies-marketing-content-block/components/dobbies-modal';

export interface IBackInStockModalProps {
    isOpen: boolean;
    onClose: () => void;
    showThankyoutext: boolean;
    isAuthenticated: boolean;
    customerEmail: string;
    onChangeEmail: (event: React.ChangeEvent<HTMLInputElement>) => void;
    isValidEmail: boolean;
    emailPlaceholderText: string;
    privacyPolicyChecked: boolean;
    onChangePrivacy: (event: React.ChangeEvent<HTMLInputElement>) => void;
    privacyPolicyUrl: string;
    onSubmit: () => void;
}

const BackInStockModal: React.FC<IBackInStockModalProps> = ({
    isOpen,
    onClose,
    showThankyoutext,
    isAuthenticated,
    customerEmail,
    onChangeEmail,
    isValidEmail,
    emailPlaceholderText,
    privacyPolicyChecked,
    onChangePrivacy,
    privacyPolicyUrl,
    onSubmit
}) => {
    const renderPopupTitle = (): JSX.Element => {
        return (
            <Node className='popup-header' tag='div'>
                <Node role='button' onClick={onClose} className='newsletter-popup-cross' />
                <Msdyn365.Text className={'popup__heading'} text={'Email me when back in stock'} />
            </Node>
        );
    };

    const renderPopupBody = (): JSX.Element => {
        return (
            <div className='newsletter-subscription-div'>
                {showThankyoutext ? (
                    <div className='thankyou-note text-center'>
                        <h3>
                            Thank you! <br /> Your request has been received.
                        </h3>
                        <p>
                            We'll notify you by email as soon as <br /> the item is back in stock.
                        </p>
                    </div>
                ) : (
                    <>
                        <div>
                            {isAuthenticated ? (
                                <p>We'll email you when the item becomes available.</p>
                            ) : (
                                <p>Please enter your email address and we'll email you when the item becomes available.</p>
                            )}
                        </div>
                        <div className='email-address-input'>
                            <input
                                type='email'
                                onChange={onChangeEmail}
                                aria-label='Email address'
                                className='email-address-input-text'
                                placeholder={emailPlaceholderText}
                                value={customerEmail}
                                required
                            />
                        </div>
                        {!isValidEmail && <div className='msc-notify-me-text'>Please input valid email</div>}
                        <div>
                            <div className='checkbox-privacy-policy'>
                                <div className='checkbox-privacy-policy-content'>
                                    <input
                                        type='checkbox'
                                        className='privacy-policy-text'
                                        aria-label='Accept Privacy Policy'
                                        aria-checked='false'
                                        onChange={onChangePrivacy}
                                        id='privacyPolicyCheckbox'
                                    />
                                    <span className='checkbox-privacy-policy-text'>
                                        <p>
                                            I accept the{' '}
                                            <a className={'privacy-link-underline'} href={privacyPolicyUrl} target='_blank'>
                                                Privacy Policy
                                            </a>
                                        </p>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className='msc-notify-me-container'>
                            <button disabled={!privacyPolicyChecked} type='button' onClick={onSubmit} className='msc-notify-me'>
                                <div>Send request</div>
                            </button>
                            <button type='button' onClick={onClose} className='msc-btn close-popup msc-btn'>
                                Cancel
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderPopupFooter = (): JSX.Element => <></>;

    if (!isOpen) {
        return null;
    }

    return (
        <DobbiesModal
            isOpen={isOpen}
            modalTitle={renderPopupTitle()}
            modalBodyElement={renderPopupBody()}
            modalFooter={renderPopupFooter()}
            className='ms-dobbies-newsletter-popup backinstock-modal modal'
        />
    );
};

export default BackInStockModal;
